import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { asyncScheduler, Observable, scheduled } from 'rxjs';

import { WizardStep } from '../../../../../shared/controls/wizard-control/wizard-step.interface';

import { ToastService } from '../../../../../shared/toast/toast.service';
import { AutoMatchingService } from '../../../../../shared/services/auto-matching-service';

import { AllClientItem, AllPeriodItem, ClientAreaByClientIdItem, MatchingService } from '../../../../../../service/proxy';

export interface MatchCounts {
    RecordsToProcess: number;
    Historical: number;
    AgeingReference: number;
    AnnexureReference: number;
    CustomerName: number;
    RegistrationNumber: number;
    Matched: number;
    Unmatched: number;
}

export interface StepStatuses {
    Historical: string;
    AgeingReference: string;
    AnnexureReference: string;
    CustomerName: string;
    RegistrationNumber: string;
}

@Component({
    selector: 'matching-auto-match',
    templateUrl: './matching-auto-match.component.html',
    styleUrls: ['./matching-auto-match.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
    ],

    providers: [
        MatchingService,
    ]
})
export class MatchingAutoMatchComponent implements WizardStep {
    @ViewChild('form')
    form!: NgForm;

    public batchId: number = 0;

    public periods: AllPeriodItem[] = [];
    public clients: AllClientItem[] = [];
    public clientAreas: ClientAreaByClientIdItem[] = [];

    public selectedPeriodId: number | null = null;
    public selectedClientId: number | null = null;
    public selectedClientAreaId: number | null = null;

    public matchingInProgress: boolean = false;
    public matchingCompleted: boolean = false;

    public matchCounts = {
        RecordsToProcess: 0,
        Historical: 0,
        AgeingReference: 0,
        AnnexureReference: 0,
        CustomerName: 0,
        RegistrationNumber: 0,
        Matched: 0,
        Unmatched: 0
    };

    public stepStatuses: StepStatuses = {
        Historical: 'Pending',
        AgeingReference: 'Pending',
        AnnexureReference: 'Pending',
        CustomerName: 'Pending',
        RegistrationNumber: 'Pending'
    };

    constructor(
        private route: ActivatedRoute,
        private cdr: ChangeDetectorRef,
        private autoMatchingService: AutoMatchingService,
        private toastService: ToastService,
    ) {}

    onActivate(): void {
        this.route.paramMap
            .subscribe(params => {
                this.batchId = Number(params.get('id'));

                if (!this.batchId) {
                    this.toastService.show('Invalid batch ID provided in the route.', 'error');
                    return;
                }
            }
        );

        this.startAutoMatching();
    }

    startAutoMatching(): void {
        this.matchingInProgress = true;

        this.autoMatchingService.startListening(this.batchId, (entry) => {
            if (entry) {
                if (entry.matchCounts) {
                    this.matchCounts = entry.matchCounts;
                }
                else {
                    this.toastService.show('No records to match.', 'info');
                    return;
                }

                if (entry.stepStatuses) {
                    this.stepStatuses = entry.stepStatuses;
                }

                if (entry.message === 'Import completed.') {
                    this.matchingInProgress = false;
                    this.matchingCompleted = true;
                    this.toastService.show('Auto-matching completed.', 'success');
                }
            }
            else {
                this.toastService.show('No progress data received.', 'warning');
            }
        }, () => {
            this.matchingInProgress = false;
            this.cdr.detectChanges();
        })
    }

    isValid(): boolean {
        return Object.values(this.stepStatuses).every(status => status === 'Done');
    }

    onNext(): Observable<boolean> {
        if (!this.isValid()) {
            this.toastService.show('Please fill in all required fields.', 'error');
            return scheduled([false], asyncScheduler);
        }

        return scheduled([true], asyncScheduler);
    }

    onPrevious(): Observable<boolean> {
        return scheduled([true], asyncScheduler);
    }
}
